/**
 * Process-wide registry of live AHP relays, keyed by task id. Ensures all
 * consumers for the same task share ONE websocket + one event sink, ref-counted
 * so the connection is torn down only after the last consumer releases it (with
 * a short grace period to survive remounts).
 *
 * Also acts as the single source of truth for "is a relay active for this
 * task", so every consumer can disable its fallback data source consistently.
 *
 * The sink that receives projected events and the Mission Control fetcher are
 * injected by the consumer, keeping this package free of any host application's
 * data layer.
 */

import type {
  AgentSelection,
  ChatState,
  MessageAttachment,
  ModelSelection,
  SessionActiveClient,
  SessionState,
  ToolCallResult,
  URI,
} from '@microsoft/agent-host-protocol'
import {
  RelayConnectionRuntime,
  type ClientTool,
  type RelayConnectionProgress,
  type RelayActiveClientRole,
  type RelayLogger,
  type RelayStatus,
  type RelayCreateSessionOptions,
  type RelayConnectionRuntimeOptions,
  type RelayChatObserver,
  type RelayChangesetObserver,
  type RelayHostInfo,
  type RelayModelCallFailure,
} from './relay-connection-runtime'
import type {ProvisioningStatus} from './session-host/resolve-project'
import type {BuildInputCompletedActionParams} from './ahp-input-actions'
import type {ResourceTokenProvider} from './protected-resource-auth'
import type {MCConnectResponse, MCFetcher} from './mission-control/mission-control-environment-client'
import type {WebSocketFactory} from './webpubsub/wps-transport'
import {buildUserMessage, type ContributedInstructionsMetadata} from './pending-message-actions'
import {
  reconcileRelaySteeringSnapshot,
  recordRelaySteeringMessage,
  reduceRelaySteeringAction,
  type RelaySteeringMessage,
} from './relay-steering-messages'
import {createRelayLease, type RelayLease, type RelayLeaseRegistryAccess} from './relay-leases'
import {
  ACTION_CHAT_TURNS_LOADED,
  ACTION_CHAT_USAGE,
  contextUsageEquals,
  readContextUsage,
  sumTokenUsage,
  tokenUsageEquals,
  type RelaySessionUsage,
} from './session-usage'
import type {RuleFile} from './instruction-plugin-source'
import {
  type SessionApprovalMode,
  APPROVAL_MODE_CONFIG_PROPERTY,
  enumIncludes,
  isApprovalModeSchemaMutable,
  readSessionApprovalState,
  readSessionPermissionMode,
  readSessionSchemaProperty,
  type SessionApprovalState,
  type SessionPermissionMode,
} from './permission-mode'
import type {
  ObservableRelaySink,
  RelayEntry,
  RelayRuntimeFactory,
  RelayRuntimeHandle,
  RelaySessionSink,
} from './relay-entry-types'
import {RelayConnectionError, relayFailureFromError, type RelayFailure} from './relay-failure'
import {buildFileUri} from './resource-write'
import {captureRelayReadiness} from './relay-readiness'
import {isDisplayableSessionTitle} from './relay-session-title'
// Keep lease types available from the registry compatibility entry point.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {RelayLease, RelayLeaseIdentity} from './relay-leases'
// Keep entry/sink types available from the registry compatibility entry point.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {ObservableRelaySink} from './relay-entry-types'
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {RelayEntry} from './relay-entry-types'
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {RelayRuntimeFactory} from './relay-entry-types'
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {RelayRuntimeHandle} from './relay-entry-types'
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {RelaySessionSink} from './relay-entry-types'

const DISPOSE_GRACE_MS = 5000
const ACTION_CHAT_TURN_CANCELLED = 'chat/turnCancelled'
const SESSION_MODE_CONFIG_PROPERTY = 'mode'

/**
 * Surfaces relay connection diagnostics to the browser console. The runtime
 * otherwise swallows connect/subscribe failures behind a noop logger, which
 * makes a silently-reconnecting relay impossible to debug from the page.
 */
const relayLogger: RelayLogger = {
  // eslint-disable-next-line no-console
  info: message => console.info(`[ahp-relay] ${message}`),
  // eslint-disable-next-line no-console
  error: message => console.error(`[ahp-relay] ${message}`),
}

const entries = new Map<string, RelayEntry>()
const listeners = new Set<() => void>()
const connectionProgressSnapshots = new WeakMap<
  RelayEntry,
  {source: RelayConnectionProgress; snapshot: RelayConnectionProgressSnapshot}
>()
const EMPTY_STEERING_MESSAGES: readonly RelaySteeringMessage[] = []
const instructionRulesByTask = new Map<string, RuleFile[]>()
const pendingInstructionRuleDiscards = new Map<string, {entry: RelayEntry; onDiscard?: () => void}>()
let relayGeneration = 0

function notify(): void {
  for (const listener of listeners) listener()
}

export function subscribeRelayRegistry(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function isRelayActive(taskId: string | undefined): boolean {
  if (!taskId) return false
  // An entry that is still in its dispose grace period continues to own the
  // shared store, so consumers must keep their fallback source disabled until
  // it is actually torn down (avoids two sources writing the same key).
  return entries.has(taskId)
}

export function getRelayStatus(taskId: string | undefined): RelayStatus | 'idle' {
  if (!taskId) return 'idle'
  return entries.get(taskId)?.status ?? 'idle'
}

export interface RelayConnectionProgressSnapshot extends RelayConnectionProgress {
  generation: number
}

export interface RelayConnectionProgressIdentity {
  environmentId: string
  sessionId: string
  channel?: string
}

export function getRelayConnectionProgress(
  taskId: string | undefined,
  identity?: RelayConnectionProgressIdentity,
): RelayConnectionProgressSnapshot | undefined {
  if (!taskId) return undefined
  const entry = entries.get(taskId)
  if (!entry?.connectionProgress) return undefined
  if (
    identity &&
    (entry.environmentId !== identity.environmentId ||
      entry.sessionId !== identity.sessionId ||
      (identity.channel !== undefined && entry.channel !== identity.channel))
  ) {
    return undefined
  }
  const cached = connectionProgressSnapshots.get(entry)
  if (cached?.source === entry.connectionProgress) return cached.snapshot
  const snapshot = {...entry.connectionProgress, generation: entry.generation}
  connectionProgressSnapshots.set(entry, {source: entry.connectionProgress, snapshot})
  return snapshot
}

// Reachable = connected now, or connected earlier and merely reconnecting. A
// terminal error/closed relay is not reachable even if it once connected.
export function getRelayReachable(
  taskId: string | undefined,
  identity?: {environmentId: string; sessionId: string; channel?: string},
): boolean {
  if (!taskId) return false
  const entry = entries.get(taskId)
  if (!entry) return false
  if (
    identity &&
    (entry.environmentId !== identity.environmentId ||
      entry.sessionId !== identity.sessionId ||
      (identity.channel !== undefined && entry.channel !== identity.channel))
  ) {
    return false
  }
  return entry.status === 'connected' || (entry.hasConnected && entry.status === 'reconnecting')
}

export function getRelayError(taskId: string | undefined): string | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.error
}

export function getRelayFailure(taskId: string | undefined): RelayFailure | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.failure
}

export function getRelayReportedError(taskId: string | undefined): string | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.reportedError
}

/** Persist an asynchronous task-scoped failure for existing relay consumers. */
export function reportRelayError(taskId: string, error: unknown): void {
  const entry = entries.get(taskId)
  if (!entry) return
  entry.reportedError = error instanceof Error ? error.message : String(error)
  notify()
}

export function isRelayPassive(taskId: string | undefined): boolean {
  if (!taskId) return false
  return entries.get(taskId)?.passive ?? false
}

/**
 * Terminated = the runtime stopped its own reconnect loop because retrying the
 * same request on a fresh connection cannot change the outcome (version
 * mismatch, MC 5xx, a deliberate host rejection).
 */
export function isRelayTerminated(taskId: string | undefined): boolean {
  if (!taskId) return false
  return entries.get(taskId)?.runtime.isTerminated?.() ?? false
}

/** Latest repo-project provisioning status for `taskId`, or `undefined` when none is underway. */
export function getRelayProvisioning(taskId: string | undefined): ProvisioningStatus | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.provisioning
}

/** Model-call failures for `taskId` keyed by turn id; persists per turn so a retried turn keeps its error. */
export function getRelayModelFailures(taskId: string | undefined): Record<string, RelayModelCallFailure> | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.modelFailures
}

export function getRelayPermissionMode(taskId: string | undefined): SessionPermissionMode | undefined {
  if (!taskId) return undefined
  return readSessionPermissionMode(entries.get(taskId)?.sessionState)
}

export function getRelayApprovalState(taskId: string | undefined): SessionApprovalState | undefined {
  if (!taskId) return undefined
  const sessionState = entries.get(taskId)?.sessionState
  if (!sessionState?.config?.schema.properties[APPROVAL_MODE_CONFIG_PROPERTY]) return undefined
  return readSessionApprovalState(sessionState)
}

export function getRelayApproveAll(taskId: string | undefined): boolean {
  return getRelayPermissionMode(taskId) === 'allow-all'
}

/**
 * Agent mode for `taskId`: the host's published `config.values.mode`, or the
 * client's pending selection until the host reports a newer value. Undefined
 * when neither is known.
 */
export function getRelayMode(taskId: string | undefined): string | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.mode
}

/** Page-local steering history for `taskId`, retained until its relay entry is disposed. */
export function getRelaySteeringMessages(taskId: string | undefined): readonly RelaySteeringMessage[] {
  if (!taskId) return EMPTY_STEERING_MESSAGES
  return entries.get(taskId)?.steeringMessages ?? EMPTY_STEERING_MESSAGES
}

export function getRelayHostInfo(taskId: string | undefined): RelayHostInfo | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.hostInfo
}

/**
 * Token totals and context-window occupancy the host reported for `taskId`, or
 * `undefined` until it reports usage. The value is cached per entry so
 * `useSyncExternalStore` consumers only re-render when the numbers change.
 */
export function getRelaySessionUsage(taskId: string | undefined): RelaySessionUsage | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.sessionUsage
}

/**
 * Returns the live observable store for `taskId`, if the registered sink
 * exposes one.
 */
export function getRelayStore(taskId: string | undefined): ObservableRelaySink | undefined {
  if (!taskId) return undefined
  const sink = entries.get(taskId)?.sink
  return sink && 'getSnapshot' in sink ? (sink as ObservableRelaySink) : undefined
}

export function getRelaySessionState(taskId: string | undefined): SessionState | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.sessionState
}

/** Internal lease adapter access; callers should prefer the opaque RelayLease API. */
export function getRelayEntry(taskId: string): RelayEntry | undefined {
  return entries.get(taskId)
}

export function observeRelayChat(taskId: string | undefined, channel: string, observer: RelayChatObserver): () => void {
  if (!taskId) throw new Error('no active relay for this task')
  const runtime = entries.get(taskId)?.runtime
  if (!runtime) throw new Error('no active relay for this task')
  if (!runtime?.observeChat) throw new Error('relay does not support additional chat subscriptions')
  return runtime.observeChat(channel, observer)
}

export function observeRelayChangeset(
  taskId: string | undefined,
  channel: string,
  observer: RelayChangesetObserver,
): () => void {
  if (!taskId) throw new Error('no active relay for this task')
  const runtime = entries.get(taskId)?.runtime
  if (!runtime) throw new Error('no active relay for this task')
  if (!runtime?.observeChangeset) throw new Error('relay does not support additional changeset subscriptions')
  return runtime.observeChangeset(channel, observer)
}

export interface AcquireRelayOptions {
  taskId: string
  environmentId: string
  sessionId: string
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: string
  /** Performs Mission Control requests for connect/reconnect. */
  fetcher: MCFetcher
  /** Builds the sink that receives this relay's snapshot + live actions. */
  createSink: (taskId: string) => RelaySessionSink
  /**
   * Opens the websocket used by the underlying WPS transport. Applied only when
   * constructing a new relay runtime; reused entries keep their original
   * websocket factory.
   */
  webSocketFactory?: WebSocketFactory
  /**
   * Waits for intent after one post-live reconnect attempt. Applied only when
   * constructing a new relay runtime; the first acquirer's value wins for a shared key.
   */
  awaitIntentAfterLive?: boolean
  /**
   * Observes relay status changes for host-app diagnostics. Applied only when
   * constructing a new relay runtime; reused entries keep their original
   * status observer.
   */
  onStatus?: (status: RelayStatus, error?: string, failure?: RelayFailure) => void
  /** Bound to the runtime's original owner; later acquisitions do not replace it. */
  onSessionLifecycle?: RelayConnectionRuntimeOptions['onSessionLifecycle']
  /**
   * Optional pre-obtained `/connect` response, consumed once on first
   * establish instead of making a fresh `/connect` call. Used by flows that
   * already minted credentials so the
   * websocket is opened against the original `client_id`'s WPS groups — a
   * second `/connect` would mint a new client_id and miss events the daemon
   * publishes to the first lane. Subsequent reconnects always use
   * `/reconnect?client_id=…` regardless.
   */
  preconnect?: MCConnectResponse
  // When set, the relay issues `createSession` once on first establish before
  // subscribing. Mutually exclusive in practice with `preconnect`.
  createSession?: RelayCreateSessionOptions
  /** Initial client tools to seed the runtime with. Can be extended dynamically via registerRelayTool. */
  clientTools?: ClientTool[]
  /** Opt this relay into the process-wide Quick Chat instruction snapshot. */
  acceptInstructionRules?: boolean
  /** See {@link RelayConnectionRuntimeOptions.activeClientRole}. */
  activeClientRole?: RelayActiveClientRole
  /** Resolves per-resource tokens when the host requires authentication. */
  resourceTokenProvider?: ResourceTokenProvider
  /** Test seam for the underlying relay runtime. Defaults to RelayConnectionRuntime. */
  createRuntime?: RelayRuntimeFactory
}

export interface AcquireRelayLeaseOptions extends Omit<AcquireRelayOptions, 'taskId'> {
  /**
   * Optional product task metadata. When omitted, the registry uses a
   * deterministic session identity key instead of making task ids part of the
   * established-session contract.
   */
  taskId?: string
}

export function acquireRelay({
  taskId,
  environmentId,
  sessionId,
  channel,
  fetcher,
  createSink,
  webSocketFactory,
  awaitIntentAfterLive,
  onStatus,
  onSessionLifecycle,
  preconnect,
  createSession,
  clientTools,
  acceptInstructionRules = false,
  activeClientRole = 'default',
  resourceTokenProvider,
  createRuntime,
}: AcquireRelayOptions): Promise<void> {
  const existing = entries.get(taskId)
  let pendingInstructionRuleDiscard: {entry: RelayEntry; onDiscard?: () => void} | undefined
  if (existing) {
    // The registry is keyed by task id, but the relay is scoped to a specific
    // session + environment + channel. If any differs, the cached runtime points
    // at the wrong channel, so tear it down and recreate rather than reuse.
    const identityMatch =
      existing.sessionId === sessionId && existing.environmentId === environmentId && existing.channel === channel
    // An observe acquire that first ran without createSession (sandbox kind not
    // yet resolved) must rebuild into a create-capable runtime once known —
    // unless the attach already connected, in which case the session exists.
    const needsCreateUpgrade = !!createSession && !existing.hasCreateSession && existing.status !== 'connected'
    // Only tighten: a subscriber-only acquire must not reuse a runtime that may
    // claim the active client, but a default acquire can share a subscriber-only one.
    const needsRuntimeReplacement =
      existing.status === 'closed' ||
      existing.runtime.isTerminated?.() === true ||
      (activeClientRole === 'never' && existing.activeClientRole !== 'never')
    if (identityMatch && !needsCreateUpgrade && !needsRuntimeReplacement) {
      if (acceptInstructionRules && existing.instructionRulesRefCount === 0) {
        existing.runtime.setInstructionRules?.(instructionRulesByTask.get(taskId) ?? [])
      }
      if (acceptInstructionRules) existing.instructionRulesRefCount += 1
      if (existing.disposeTimer) {
        clearTimeout(existing.disposeTimer)
        existing.disposeTimer = undefined
      }
      existing.refCount += 1
      return Promise.resolve()
    }
    if (existing.disposeTimer) clearTimeout(existing.disposeTimer)
    const pending = pendingInstructionRuleDiscards.get(taskId)
    if (pending?.entry === existing) pendingInstructionRuleDiscard = pending
    entries.delete(taskId)
    existing.sink.dispose()
    void existing.runtime.dispose?.()
  }

  const sink = createSink(taskId)
  const entry: RelayEntry = {
    refCount: 1,
    sink,
    sessionId,
    environmentId,
    channel,
    hasCreateSession: !!createSession,
    activeClientRole,
    instructionRulesRefCount: acceptInstructionRules ? 1 : 0,
    status: 'idle',
    hasConnected: false,
    passive: false,
    passiveKnown: false,
    generation: ++relayGeneration,
    steeringMessages: EMPTY_STEERING_MESSAGES,
    actionListeners: new Set(),
    runtime: undefined as unknown as RelayRuntimeHandle,
  }

  // Hand cache ownership to the sink before any events arrive.
  sink.start()

  // When pre-seeded, the runtime's first establish reuses the supplied
  // response instead of calling /connect. The lambda is consumed at most
  // once: any later reconnect cycle goes through mcReconnect (default uses
  // the fetcher + the original client_id), so the URL change is automatic.
  const mcConnect = preconnect ? () => Promise.resolve(preconnect) : undefined

  /**
   * Recompute the cached usage snapshot, replacing it only when the numbers
   * actually change so registry subscribers are not woken for equal values.
   */
  const refreshSessionUsage = (chatState?: ChatState): void => {
    const state = chatState ?? ('getSnapshot' in sink ? (sink as ObservableRelaySink).getSnapshot() : undefined)
    const tokens = sumTokenUsage(state)
    const context = readContextUsage(entry.sessionState?._meta)
    const current = entry.sessionUsage
    if (tokenUsageEquals(current?.tokens, tokens) && contextUsageEquals(current?.context, context)) return
    entry.sessionUsage = tokens || context ? {...(tokens ? {tokens} : {}), ...(context ? {context} : {})} : undefined
    notify()
  }

  const runtimeOptions: RelayConnectionRuntimeOptions = {
    environmentId,
    sessionId,
    fetcher,
    logger: relayLogger,
    ...(channel ? {channel} : {}),
    ...(mcConnect ? {mcConnect} : {}),
    ...(webSocketFactory ? {webSocketFactory} : {}),
    ...(awaitIntentAfterLive ? {awaitIntentAfterLive} : {}),
    ...(createSession ? {createSession} : {}),
    ...(clientTools ? {clientTools} : {}),
    ...(activeClientRole === 'never' ? {activeClientRole} : {}),
    ...(resourceTokenProvider ? {resourceTokenProvider} : {}),
    ...(onSessionLifecycle ? {onSessionLifecycle} : {}),
    onSnapshot: state => {
      if (!entry.readiness?.chat) {
        const chat = captureRelayReadiness('snapshot')
        if (chat) entry.readiness = {...entry.readiness, chat}
      }
      sink.applySnapshot(state)
      refreshSessionUsage(state)
      const next = reconcileRelaySteeringSnapshot(entry.steeringMessages, state)
      if (next !== entry.steeringMessages) {
        entry.steeringMessages = next
        notify()
      }
    },
    onAction: env => {
      const previousState = 'getSnapshot' in sink ? (sink as ObservableRelaySink).getSnapshot() : undefined
      const next = reduceRelaySteeringAction(entry.steeringMessages, env, previousState)
      if (!env.rejectionReason) {
        if (!entry.readiness?.chat) {
          const chat = captureRelayReadiness('action')
          if (chat) entry.readiness = {...entry.readiness, chat}
        }
        if (env.action.type === 'session/titleChanged' && sink.applySessionTitle) {
          // Metadata can already include a newer root title than this buffered action.
          const title = entry.sessionState?.title ?? env.action.title
          if (isDisplayableSessionTitle(title, sessionId)) sink.applySessionTitle(title)
        } else {
          sink.applyAction(env)
        }
        const state = 'getSnapshot' in sink ? (sink as ObservableRelaySink).getSnapshot() : undefined
        // Refresh before notifying, so a listener reading the registry sees the
        // usage derived from the action it is being handed.
        if (env.action.type === ACTION_CHAT_USAGE || env.action.type === ACTION_CHAT_TURNS_LOADED) {
          refreshSessionUsage(state)
        }
        for (const listener of entry.actionListeners) listener(env, previousState, state)
      } else if (env.action.type === ACTION_CHAT_TURN_CANCELLED) {
        sink.restoreCancelledTurn?.((env.action as {turnId?: string}).turnId)
      }
      if (next !== entry.steeringMessages) {
        entry.steeringMessages = next
        notify()
      }
    },
    onStatus: (status, error, failure) => {
      entry.status = status
      if (status === 'connected') {
        entry.hasConnected = true
        entry.error = undefined
        entry.failure = undefined
      } else if (status === 'connecting') {
        entry.error = error
        entry.failure = failure ?? entry.failure
      } else {
        entry.error = error ?? (status === 'reconnecting' ? entry.error : undefined)
        entry.failure = failure ?? (status === 'reconnecting' ? entry.failure : undefined)
      }
      if (failure) onStatus?.(status, error, failure)
      else onStatus?.(status, error)
      notify()
    },
    onConnectionProgress: progress => {
      const current = entry.connectionProgress
      if (current?.phase === progress.phase && current.retryAttempt === progress.retryAttempt) return
      entry.connectionProgress = progress
      notify()
    },
    onPassiveChange: passive => {
      if (!entry.passiveKnown || entry.passive !== passive) {
        const role = captureRelayReadiness('action')
        if (role) entry.readiness = {...entry.readiness, role}
      }
      entry.passive = passive
      entry.passiveKnown = true
      notify()
    },
    onProvisioning: status => {
      entry.provisioning = status
      notify()
    },
    onModelCallFailure: (failure, turnId) => {
      // Replace the map (not mutate) so useSyncExternalStore sees a new snapshot.
      entry.modelFailures = {...(entry.modelFailures ?? {}), [turnId]: failure}
      notify()
    },
    onSessionState: state => {
      if (
        !entry.readiness?.session ||
        entry.sessionState?.lifecycle !== state.lifecycle ||
        entry.sessionState?.defaultChat !== state.defaultChat
      ) {
        const session = captureRelayReadiness('snapshot')
        if (session) entry.readiness = {...entry.readiness, session}
      }
      entry.sessionState = state
      const hostMode = state.config?.values?.[SESSION_MODE_CONFIG_PROPERTY]
      if (typeof hostMode === 'string' && hostMode !== entry.hostMode) {
        entry.hostMode = hostMode
        entry.mode = hostMode
      }
      if (isDisplayableSessionTitle(state.title, sessionId)) sink.applySessionTitle?.(state.title)
      refreshSessionUsage()
      notify()
    },
    onHostInfo: info => {
      entry.hostInfo = info
      notify()
    },
  }
  const runtime = createRuntime ? createRuntime(runtimeOptions) : new RelayConnectionRuntime(runtimeOptions)
  entry.runtime = runtime
  const instructionRules = instructionRulesByTask.get(taskId)
  if (acceptInstructionRules && instructionRules) runtime.setInstructionRules?.(instructionRules)
  entries.set(taskId, entry)
  if (pendingInstructionRuleDiscard) {
    pendingInstructionRuleDiscards.set(taskId, {...pendingInstructionRuleDiscard, entry})
    discardInstructionRulesIfUnused(taskId, entry)
  }
  notify()

  return runtime.connect()
}

export function releaseRelayEntry(registryKey: string, entry: RelayEntry): void {
  if (entries.get(registryKey) !== entry) return
  if (entry.disposeTimer) {
    clearTimeout(entry.disposeTimer)
    entry.disposeTimer = undefined
  }
  entry.refCount -= 1
  if (entry.refCount > 0) return

  entry.disposeTimer = setTimeout(() => {
    const current = entries.get(registryKey)
    if (current !== entry || current.refCount > 0) return
    entries.delete(registryKey)
    current.sink.dispose()
    void current.runtime.dispose?.()
    notify()
  }, DISPOSE_GRACE_MS)
}

export function cancelRelayEntryConnection(registryKey: string, entry: RelayEntry): void {
  if (entries.get(registryKey) !== entry) return
  if (entry.disposeTimer) {
    clearTimeout(entry.disposeTimer)
    entry.disposeTimer = undefined
  }
  entry.refCount -= 1
  if (entry.refCount > 0) return

  entries.delete(registryKey)
  entry.sink.dispose()
  void entry.runtime.dispose?.()
  notify()
}

/**
 * Acquire an identity-bound lease for a mounted established-session surface.
 * The old task-id registry APIs remain available for callers that have not
 * migrated yet.
 */
export async function acquireRelayLease(
  options: AcquireRelayLeaseOptions,
  acquisition: {signal?: AbortSignal; onConnectionProgress?: (progress: RelayConnectionProgress) => void} = {},
): Promise<RelayLease> {
  const registryKey =
    options.taskId ?? `${options.environmentId}\u0000${options.sessionId}\u0000${options.channel ?? ''}`
  const connection = acquireRelay({...options, taskId: registryKey})
  const entry = entries.get(registryKey)
  if (!entry) throw new Error('relay lease was not registered')

  const lease = createRelayLease(
    entry,
    {
      ...(options.taskId ? {taskId: options.taskId} : {}),
      environmentId: options.environmentId,
      sessionId: options.sessionId,
      ...(options.channel ? {channel: options.channel} : {}),
    },
    registryKey,
    {
      getRelayEntry,
      releaseRelayEntry: (key, currentEntry) => {
        if (options.acceptInstructionRules && currentEntry.instructionRulesRefCount > 0) {
          currentEntry.instructionRulesRefCount -= 1
          discardInstructionRulesIfUnused(key, currentEntry)
        }
        releaseRelayEntry(key, currentEntry)
      },
      cancelRelayEntryConnection: (key, currentEntry) => {
        if (options.acceptInstructionRules && currentEntry.instructionRulesRefCount > 0) {
          currentEntry.instructionRulesRefCount -= 1
          discardInstructionRulesIfUnused(key, currentEntry)
        }
        cancelRelayEntryConnection(key, currentEntry)
      },
      subscribeRelayRegistry,
      getRelayStore,
      getRelayStatus: key => getRelayStatus(key),
      getRelayConnectionProgress: key => getRelayConnectionProgress(key),
      getRelayReachable: key => getRelayReachable(key),
      getRelayError: key => getRelayError(key),
      getRelayFailure: key => getRelayFailure(key),
      getRelayModelFailures: key => getRelayModelFailures(key),
      getRelaySessionState: key => getRelaySessionState(key),
      isRelayPassive: key => isRelayPassive(key),
      getRelaySteeringMessages: key => getRelaySteeringMessages(key),
      steerRelayMessage: (key, text, attachments, model, steeringId, agent, contributedInstructions) =>
        steerRelayMessageWithAgent(key, text, attachments, model, steeringId, agent, contributedInstructions),
      setRelayApproveAll: (key, enabled) => setRelayApproveAll(key, enabled),
      setRelayApprovalMode: (key, mode) => setRelayApprovalMode(key, mode),
      setRelayMode: (key, mode) => setRelayMode(key, mode),
      ensureRelayLive: (key, onReconnect) => ensureRelayLive(key, onReconnect),
      whenRelayConnected: (key, timeoutMs, provisioningTimeoutMs, signal) =>
        whenRelayConnected(key, timeoutMs, provisioningTimeoutMs, signal),
    } satisfies RelayLeaseRegistryAccess,
  )

  let rejectProgress: (error: unknown) => void = () => {}
  const progressFailed = new Promise<never>((_resolve, reject) => {
    rejectProgress = reject
  })
  const reportConnectionProgress = () => {
    try {
      const progress = entry.connectionProgress
      if (progress) acquisition.onConnectionProgress?.(progress)
    } catch (error) {
      rejectProgress(error)
    }
  }
  let unsubscribeConnectionProgress = () => {}

  const signal = acquisition.signal
  let onAbort = () => {}
  try {
    if (signal?.aborted) throw signal.reason
    if (acquisition.onConnectionProgress) {
      unsubscribeConnectionProgress = subscribeRelayRegistry(reportConnectionProgress)
      reportConnectionProgress()
    }
    if (signal?.aborted) throw signal.reason
    if (!signal) {
      await Promise.race([connection, progressFailed])
      return lease
    }

    const aborted = new Promise<never>((_resolve, reject) => {
      onAbort = () => reject(signal.reason)
      signal.addEventListener('abort', onAbort, {once: true})
    })
    await Promise.race([connection, aborted, progressFailed])
    if (signal.aborted) throw signal.reason
    return lease
  } catch (error) {
    if (signal?.aborted && error === signal.reason) lease.cancelConnection()
    else lease.release()
    throw error
  } finally {
    signal?.removeEventListener('abort', onAbort)
    unsubscribeConnectionProgress()
  }
}

/** Dispatch a follow-up user turn over the live relay for a task. */
export function dispatchRelayTurn(
  taskId: string | undefined,
  text: string,
  attachments?: MessageAttachment[],
  model?: ModelSelection,
  contributedInstructions?: ContributedInstructionsMetadata,
): string {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  const turnId =
    contributedInstructions === undefined
      ? model
        ? entry.runtime.dispatchTurn(text, attachments, model)
        : entry.runtime.dispatchTurn(text, attachments)
      : entry.runtime.dispatchTurn(text, attachments, model, undefined, contributedInstructions)
  if (!turnId) throw new Error('active relay did not return a dispatched turn id')
  return turnId
}

/** Accept, decline, or cancel a pending input request over the live relay. */
export function dispatchRelayInputCompleted(taskId: string | undefined, params: BuildInputCompletedActionParams): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.completeInput) throw new Error('active relay cannot complete input requests')
  entry.runtime.completeInput(params)
}

/** Queue a follow-up user message over the live relay for a task. */
export function queueRelayMessage(
  taskId: string | undefined,
  text: string,
  attachments?: MessageAttachment[],
  model?: ModelSelection,
  contributedInstructions?: ContributedInstructionsMetadata,
): string {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.queueMessage) throw new Error('active relay cannot queue messages')
  return contributedInstructions === undefined
    ? entry.runtime.queueMessage(text, attachments, model)
    : entry.runtime.queueMessage(text, attachments, model, undefined, contributedInstructions)
}

/** Steer the active turn over the live relay and retain it in page-local timeline history. */
export function steerRelayMessage(
  taskId: string | undefined,
  text: string,
  attachments?: MessageAttachment[],
  model?: ModelSelection,
  steeringId?: string,
  contributedInstructions?: ContributedInstructionsMetadata,
): string {
  return steerRelayMessageWithAgent(taskId, text, attachments, model, steeringId, undefined, contributedInstructions)
}

function steerRelayMessageWithAgent(
  taskId: string | undefined,
  text: string,
  attachments?: MessageAttachment[],
  model?: ModelSelection,
  steeringId?: string,
  agent?: AgentSelection,
  contributedInstructions?: ContributedInstructionsMetadata,
): string {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.steerMessage) throw new Error('active relay cannot steer messages')

  const id = steeringId ?? crypto.randomUUID()
  if (contributedInstructions === undefined) {
    if (agent === undefined) {
      entry.runtime.steerMessage(text, attachments, model, id)
    } else {
      entry.runtime.steerMessage(text, attachments, model, id, agent)
    }
  } else {
    entry.runtime.steerMessage(text, attachments, model, id, agent, contributedInstructions)
  }
  const state = getRelayStore(taskId)?.getSnapshot()
  const next = recordRelaySteeringMessage(
    entry.steeringMessages,
    {id, message: buildUserMessage(text, attachments, model, agent, contributedInstructions)},
    state,
  )
  if (next !== entry.steeringMessages) {
    entry.steeringMessages = next
    notify()
  }
  return id
}

/** Remove a queued user message over the live relay for a task. */
export function removeRelayQueuedMessage(taskId: string | undefined, id: string): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.removeQueuedMessage) throw new Error('active relay cannot remove queued messages')
  entry.runtime.removeQueuedMessage(id)
}

export async function setRelayApprovalMode(taskId: string | undefined, mode: SessionApprovalMode): Promise<void> {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  const property = readSessionSchemaProperty(entry.sessionState, APPROVAL_MODE_CONFIG_PROPERTY)
  if (property) {
    if (!isApprovalModeSchemaMutable(property)) {
      throw new Error('host permission mode config is not session-mutable')
    }
    if (!enumIncludes(property.enum, mode)) {
      throw new Error('host does not advertise the requested permission mode')
    }
    const available = readSessionApprovalState(entry.sessionState).available
    if (mode !== 'manual' && !available?.includes(mode)) {
      throw new Error('host does not advertise the requested permission mode as available')
    }
    if (!entry.runtime.setSessionApprovalMode) throw new Error('active relay cannot set permission mode')
    await entry.runtime.setSessionApprovalMode(mode)
    return
  }
  if (mode === 'assisted') throw new Error('host does not advertise approval mode configuration')
  if (!entry.runtime.setSessionApproveAll) throw new Error('active relay cannot set permission mode')
  await entry.runtime.setSessionApproveAll(mode === 'allow-all')
}

/** Toggle per-session auto-approval ("allow all") on the host over the live relay for a task. */
export function setRelayApproveAll(taskId: string | undefined, enabled: boolean): Promise<void> {
  return setRelayApprovalMode(taskId, enabled ? 'allow-all' : 'manual')
}

/** Set the per-session agent mode without changing the independent allow-all setting. */
export async function setRelayMode(taskId: string | undefined, mode: string): Promise<void> {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.setSessionMode) throw new Error('active relay cannot set mode')
  const previousMode = entry.mode
  entry.mode = mode
  notify()
  try {
    await entry.runtime.setSessionMode(mode)
  } catch (err) {
    entry.mode = previousMode
    notify()
    throw err
  }
}

/** Respond to a pending tool-call confirmation over the live relay for a task. */
export function confirmRelayToolCall(
  taskId: string | undefined,
  confirmation: {
    approved: boolean
    toolCallId: string
    turnId: string
    resultConfirmation?: boolean
    selectedOptionId?: string
    forSession?: boolean
    chat?: URI
  },
): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.confirmToolCall) throw new Error('active relay cannot confirm tool calls')
  entry.runtime.confirmToolCall(confirmation)
}

export function publishRelayActiveClient(taskId: string | undefined, activeClient: SessionActiveClient): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.publishActiveClient) throw new Error('active relay cannot publish active clients')
  entry.runtime.publishActiveClient(activeClient)
}

export function publishRelayInstructionRules(taskId: string | undefined, rules: RuleFile[]): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.setInstructionRules) throw new Error('active relay cannot publish instruction rules')
  entry.runtime.setInstructionRules(rules)
}

/** Publish and seed the latest custom instructions for one Quick Chat task. */
export function setRelayInstructionRules(taskId: string, rules: RuleFile[]): void {
  const instructionRules = rules.map(rule => ({...rule}))
  if (instructionRules.length > 0) {
    instructionRulesByTask.set(taskId, instructionRules)
  } else {
    instructionRulesByTask.delete(taskId)
  }
  const entry = entries.get(taskId)
  if (entry && entry.instructionRulesRefCount > 0) entry.runtime.setInstructionRules?.(instructionRules)
}

export function discardRelayInstructionRulesWhenUnused(
  taskId: string,
  expectedEntry: RelayEntry | undefined,
  onDiscard?: () => void,
): void {
  const entry = entries.get(taskId)
  if (entry !== expectedEntry) return
  const pending = pendingInstructionRuleDiscards.get(taskId)
  const notifyDiscard = onDiscard ?? (pending?.entry === entry ? pending?.onDiscard : undefined)
  if (!entry || entry.instructionRulesRefCount === 0) {
    setRelayInstructionRules(taskId, [])
    entry?.runtime.setInstructionRules?.([])
    pendingInstructionRuleDiscards.delete(taskId)
    notifyDiscard?.()
    return
  }
  pendingInstructionRuleDiscards.set(taskId, {entry, onDiscard: notifyDiscard})
}

function discardInstructionRulesIfUnused(taskId: string, entry: RelayEntry): void {
  const pending = pendingInstructionRuleDiscards.get(taskId)
  if (entry.instructionRulesRefCount > 0 || pending?.entry !== entry) return
  pendingInstructionRuleDiscards.delete(taskId)
  if (entries.get(taskId) !== entry) return
  setRelayInstructionRules(taskId, [])
  entry.runtime.setInstructionRules?.([])
  pending.onDiscard?.()
}

export function getRelayClientId(taskId: string | undefined): string {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (!entry.runtime.getClientId) throw new Error('active relay cannot report its client id')
  return entry.runtime.getClientId()
}

export function renameRelaySession(taskId: string | undefined, title: string): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.renameSession) throw new Error('active relay cannot rename sessions')
  entry.runtime.renameSession(title)
}

export function completeRelayToolCall({
  taskId,
  turnId,
  toolCallId,
  result,
}: {
  taskId: string | undefined
  turnId: string
  toolCallId: string
  result: ToolCallResult
}): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.completeToolCall) throw new Error('active relay cannot complete tool calls')
  entry.runtime.completeToolCall({turnId, toolCallId, result})
}

/**
 * Cancel the active turn on the live relay for a task. No-op returning `false`
 * when no relay is registered (e.g. a click after teardown).
 */
export function cancelRelayTurn(taskId: string | undefined, turnId?: string): boolean {
  if (!taskId) return false
  const entry = entries.get(taskId)
  if (!entry) return false
  if (entry.passive) return false
  if (!entry.runtime.cancelTurn) return false
  const accepted = entry.runtime.cancelTurn(turnId)
  if (accepted) markSinkTurnCancelled(entry.sink, turnId)
  return accepted
}

/**
 * Tell the sink the user stopped a turn, once the cancel has been dispatched,
 * so it stops rendering that turn's buffered tokens.
 */
export function markSinkTurnCancelled(sink: RelaySessionSink, turnId: string | undefined): void {
  const target =
    turnId ?? ('getSnapshot' in sink ? (sink as ObservableRelaySink).getSnapshot()?.activeTurn?.id : undefined)
  sink.markTurnCancelled?.(target)
}

/** Register a client tool dynamically on the relay for a task. Auto-announces to the host. */
export function registerRelayTool(taskId: string | undefined, tool: ClientTool): void {
  if (!taskId) return
  const entry = entries.get(taskId)
  if (!entry) return
  entry.runtime.registerTool?.(tool)
}

/** Unregister a client tool by name on the relay for a task. Auto-announces to the host. */
export function unregisterRelayTool(taskId: string | undefined, name: string): void {
  if (!taskId) return
  const entry = entries.get(taskId)
  if (!entry) return
  entry.runtime.unregisterTool?.(name)
}

/**
 * Force the relay for `taskId` to drop and re-establish its subscription,
 * so the next `subscribe` returns a fresh snapshot from the daemon. Useful
 * when a new consumer mounts on top of an already-active runtime whose
 * sink's cache may have missed action notifications (e.g. because the
 * previous consumer was torn down mid-stream).
 *
 * No-op if no relay is registered for `taskId`.
 */
export function refreshRelaySnapshot(taskId: string | undefined): void {
  if (!taskId) return
  const entry = entries.get(taskId)
  if (!entry) return
  // Tolerate runtime stubs (e.g. test fakes) that don't implement refresh().
  if (typeof entry.runtime.refresh !== 'function') return
  void entry.runtime.refresh()
}

/**
 * Confirm the relay for `taskId` can still reach the host, reconnecting it if
 * the lane was reclaimed while the tab was idle.
 *
 * Callers should await this before dispatching after a period of inactivity.
 * `onReconnect` runs before a slow reconnect begins. Await
 * {@link waitForConnected} afterwards when this resolves `true`.
 *
 * No-op if no relay is registered for `taskId`.
 *
 * Unlike the read accessors in this module, `taskId` is required. A `false`
 * here reads as "no reconnect was needed, go ahead and dispatch", so silently
 * accepting a missing id would hand back an all-clear for a relay we never
 * looked at.
 */
export async function ensureRelayLive(taskId: string, onReconnect?: () => void): Promise<boolean> {
  const entry = entries.get(taskId)
  if (!entry) return false
  // Tolerate runtime stubs (e.g. test fakes) that don't implement ensureLive().
  if (typeof entry.runtime.ensureLive !== 'function') return false
  return await entry.runtime.ensureLive(onReconnect)
}

/**
 * Resolves once the relay for `taskId` reaches the `connected` state, so
 * callers that hold an `acquireRelay` ref can safely
 * {@link dispatchRelayTurn} immediately after.
 *
 * A transient `error` or `reconnecting` state is tolerated — the runtime
 * automatically retries with backoff unless opted into post-live
 * `awaiting-intent`, which waits for `ensureLive` after one reconnect attempt.
 * A terminal `error` whose runtime
 * reports `isTerminated()`, `closed`, or a terminal repo-project provisioning
 * failure rejects immediately.
 *
 * Reaching `connected` can take far longer than a plain connect when the relay
 * is provisioning a repo project (catalogue match or clone) before
 * `createSession`, so once provisioning starts the budget expands to
 * `provisioningTimeoutMs`.
 */
export function whenRelayConnected(
  taskId: string | undefined,
  timeoutMs = 120_000,
  provisioningTimeoutMs = 190_000,
  signal?: AbortSignal,
): Promise<void> {
  if (!taskId) return Promise.reject(new Error('no active relay for this task'))
  const initial = entries.get(taskId)
  if (!initial) return Promise.reject(new Error('no active relay for this task'))
  if (initial.status === 'connected') return Promise.resolve()
  if (signal?.aborted) return Promise.reject(signal.reason)

  return new Promise((resolve, reject) => {
    let settled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // Measured from when provisioning first became active, so a late clone still gets its full budget.
    let provisioningStartedAt: number | undefined
    const startedAt = Date.now()

    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
      unsubscribe()
      fn()
    }

    const scheduleTimeout = () => {
      if (signal) return
      if (timer) clearTimeout(timer)
      const budget =
        provisioningStartedAt !== undefined
          ? provisioningStartedAt + provisioningTimeoutMs - Date.now()
          : startedAt + timeoutMs - Date.now()
      const remaining = Math.max(0, budget)
      const limit = provisioningStartedAt !== undefined ? provisioningTimeoutMs : timeoutMs
      timer = setTimeout(() => {
        const message = `relay did not connect within ${limit}ms`
        const failure = entries.get(taskId)?.failure ?? {kind: 'unknown'}
        finish(() => reject(new RelayConnectionError(message, failure)))
      }, remaining)
    }

    const evaluate = () => {
      if (settled) return
      const entry = entries.get(taskId)
      if (!entry) return finish(() => reject(new Error('relay was torn down before connecting')))

      if (entry.provisioning && entry.provisioning.phase !== 'ready') {
        // A terminal clone/match failure rejects immediately rather than waiting out the timeout.
        if (entry.provisioning.phase === 'failed') {
          const message = entry.provisioning.error ?? 'repository project provisioning failed'
          const failure = entry.failure ?? relayFailureFromError(new Error(message))
          return finish(() => reject(new RelayConnectionError(message, failure)))
        }
        if (provisioningStartedAt === undefined) {
          provisioningStartedAt = Date.now()
          scheduleTimeout()
        }
      }

      if (entry.status === 'connected') return finish(resolve)
      if (entry.status === 'closed') {
        const message = entry.error ?? 'relay closed before connecting'
        return finish(() => reject(new RelayConnectionError(message, entry.failure ?? {kind: 'unknown'})))
      }
      if (entry.status === 'error' && entry.runtime.isTerminated?.()) {
        const message = entry.error ?? 'relay failed before connecting'
        return finish(() => reject(new RelayConnectionError(message, entry.failure ?? {kind: 'unknown'})))
      }
    }

    const onAbort = () => finish(() => reject(signal?.reason))
    const unsubscribe = subscribeRelayRegistry(evaluate)
    signal?.addEventListener('abort', onAbort, {once: true})
    scheduleTimeout()
    evaluate()
  })
}

export function whenRelayPassiveResolved(taskId: string | undefined, timeoutMs = 30_000): Promise<boolean> {
  if (!taskId) return Promise.reject(new Error('no active relay for this task'))
  const initial = entries.get(taskId)
  if (!initial) return Promise.reject(new Error('no active relay for this task'))
  if (initial.passiveKnown) return Promise.resolve(initial.passive)

  return new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(
      () => finish(() => reject(new Error(`relay passivity was not resolved within ${timeoutMs}ms`))),
      timeoutMs,
    )

    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe()
      fn()
    }

    const evaluate = () => {
      const entry = entries.get(taskId)
      if (!entry) return finish(() => reject(new Error('relay was torn down before passivity resolved')))
      if (entry.passiveKnown) return finish(() => resolve(entry.passive))
    }

    const unsubscribe = subscribeRelayRegistry(evaluate)
    evaluate()
  })
}

export function releaseRelay(taskId: string, options: {acceptInstructionRules?: boolean} = {}): void {
  const entry = entries.get(taskId)
  if (!entry) return
  if (options.acceptInstructionRules && entry.instructionRulesRefCount > 0) {
    entry.instructionRulesRefCount -= 1
    discardInstructionRulesIfUnused(taskId, entry)
  }
  releaseRelayEntry(taskId, entry)
}

/** Immediately dispose the relay for `taskId`, bypassing the remount grace period. */
export function disposeRelayNow(taskId: string): void {
  const entry = entries.get(taskId)
  if (!entry) return
  if (entry.disposeTimer) clearTimeout(entry.disposeTimer)
  const pending = pendingInstructionRuleDiscards.get(taskId)
  if (pending?.entry === entry) {
    pendingInstructionRuleDiscards.delete(taskId)
    setRelayInstructionRules(taskId, [])
    pending.onDiscard?.()
  }
  entries.delete(taskId)
  entry.sink.dispose()
  void entry.runtime.dispose?.()
  notify()
}

/** Test-only: force-clear all relays without grace period. */
export function __resetRelayRegistry(): void {
  for (const [, entry] of entries) {
    if (entry.disposeTimer) clearTimeout(entry.disposeTimer)
    entry.sink.dispose()
    void entry.runtime.dispose?.()
  }
  entries.clear()
  instructionRulesByTask.clear()
  for (const pending of pendingInstructionRuleDiscards.values()) pending.onDiscard?.()
  pendingInstructionRuleDiscards.clear()
  notify()
}

/**
 * A filesystem writer bound to the live relay for `taskId`, or `undefined` when this task
 * has no relay, the relay is read-only, or the host never reported a working directory.
 *
 * `undefined` is the signal that a surface must fall back to inlining attachments: plain
 * Copilot Chat has no sandbox at all, and a passive (observer) connection must not write.
 */
/**
 * Remembers which `(taskId, reason)` pairs we have already reported so a hook
 * that re-renders does not repeat the same line on every render.
 */
const reportedWriterGaps = new Set<string>()

/**
 * Sandbox uploads degrade to prompt-inlining whenever a writer is unavailable,
 * which is correct but indistinguishable from the feature being broken. Name
 * the specific missing precondition once per task so the fallback is visible.
 */
function reportUnavailableWriter(taskId: string, reason: string): undefined {
  const key = `${taskId}:${reason}`
  if (!reportedWriterGaps.has(key)) {
    reportedWriterGaps.add(key)
    relayLogger.info(`sandbox uploads unavailable for task ${taskId}: ${reason}`)
  }
  return undefined
}

export function getRelayResourceWriter(
  taskId: string | undefined,
  uploadDirectory: string,
): SandboxResourceWriter | undefined {
  if (!taskId) return undefined
  const entry = entries.get(taskId)
  if (!entry) return reportUnavailableWriter(taskId, 'no relay connection is registered for this task')
  if (entry.passive) return reportUnavailableWriter(taskId, 'the relay connection is passive (read-only)')

  const {runtime, hostInfo} = entry
  const root = hostInfo?.defaultDirectory
  if (!root) return reportUnavailableWriter(taskId, 'the host has not reported a defaultDirectory yet')
  if (!runtime.writeHostResource || !runtime.createHostDirectory) {
    return reportUnavailableWriter(taskId, 'the relay runtime does not expose resource-write methods')
  }

  const directory = buildFileUri(root, ...uploadDirectory.split('/'))
  const write = runtime.writeHostResource.bind(runtime)
  const mkdir = runtime.createHostDirectory.bind(runtime)
  const remove = runtime.deleteHostResource?.bind(runtime)
  const stat = runtime.resolveHostResource?.bind(runtime)

  return {
    directory,
    resolve: filename => buildFileUri(root, ...uploadDirectory.split('/'), filename),
    write: params => write(params),
    mkdir: uri => mkdir(uri),
    stat: stat ? uri => stat(uri) : undefined,
    remove: async uri => {
      await remove?.(uri)
    },
  }
}

export interface SandboxResourceWriter {
  /** Absolute `file:` URI of the directory uploads land in. */
  directory: string
  resolve(filename: string): string
  write(params: {
    uri: string
    data: string
    encoding: 'base64' | 'utf-8'
    contentType?: string
    createOnly?: boolean
    mode?: 'truncate' | 'append' | 'insert'
    position?: number
  }): Promise<void>
  mkdir(uri: string): Promise<void>
  remove(uri: string): Promise<void>
  /** Absent on hosts too old to implement `resourceResolve`. */
  stat?(uri: string): Promise<{uri: string; size?: number}>
}
